import { useContext, useMemo, useState } from 'react';
import { ILink } from '../../../interfaces/Link';
import FaviconLoader from '../FaviconFetcher';
import { getLinkLabel } from '../../../utils/linkUtils';
import { defaultFavicon } from '../../../utils/iconUtils';
import { getCountryFlag } from '../../../utils/countryUtils';
import useDeviceDetect from '../../../helpers/screens';
import LinkViewChart from '../charts/LinkViewChart';
import YoutubeAnalytics from '../../dashboard/YoutubeAnalytics';
import IconButton from '../IconButton';
import { AnalyticsData } from '../../../pages/Analytics';
import { UserContext } from '../../../context/UserContext';
import useI18n from '../../../hooks/useI18n';
import { useUserLinkAnalyticsQuery } from '../../../hooks/queries/useAnalyticsQueries';

interface Prop {
  data: ILink;
  handleDetailsModalClose: () => void;
  hasSubscription: boolean;
  isConnectedWithYoutube: boolean;
}
const interval = {
  daily: 'day' as const,
  weekly: 'week' as const,
  monthly: 'month' as const,
  yearly: 'year' as const,
};

const LinkDetailsCard: React.FC<Prop> = ({
  data,
  handleDetailsModalClose,
  hasSubscription,
  isConnectedWithYoutube,
}) => {
  const [linkLogo, setLinkLogo] = useState('');
  const isMobile = useDeviceDetect();
  const isYoutubeLink = data.targetSite.toLowerCase() === 'youtube';
  const [view, setView] = useState<'daily' | 'weekly' | 'monthly' | 'yearly'>(
    'weekly',
  );
  const userContext = useContext(UserContext);
  const { t } = useI18n();

  if (!userContext) {
    throw new Error('useContext must be used within a UserProvider');
  }
  const { user } = userContext;

  const { data: rawAnalyticsData } = useUserLinkAnalyticsQuery(
    user?._id || '',
    data._id,
    interval[view],
    !!user?._id && !!data._id,
  );

  const defaultAnalyticsData: AnalyticsData = {
    bestPerformingPlatform: {
      platformName: null,
      changeInPercentage: 0,
    },
    countryCounts: [],
    deviceTypeDistribution: {
      mobile: 0,
      tablet: 0,
      pc: 0,
    },
    timeFrameView: [],
    topCity: null,
    topCountry: null,
    topSources: [],
    totalClicked: {
      count: 0,
      changeInPercentage: 0,
    },
    bestTimeToEngage: null,
  };

  const processCountryName = (countryCode: string | null): string | null => {
    if (!countryCode) return null;

    try {
      const regionNames = new Intl.DisplayNames(['en'], {
        type: 'region',
      });
      return regionNames.of(countryCode);
    } catch (error) {
      console.error('Error processing country name', error);
      return countryCode;
    }
  };

  const getCountryFlagSafely = (countryName: string | null): string => {
    if (!countryName) return defaultFavicon;

    try {
      return getCountryFlag(countryName);
    } catch (error) {
      console.error('Error getting country flag', error);
      return defaultFavicon;
    }
  };

  const analyticsData = useMemo(() => {
    if (!rawAnalyticsData) return defaultAnalyticsData;

    const formattedData = { ...rawAnalyticsData } as AnalyticsData;

    if (formattedData.topCountry) {
      formattedData.topCountry = processCountryName(formattedData.topCountry);
    }

    return formattedData;
  }, [rawAnalyticsData]);

  const topCountryFlag = useMemo(
    () => getCountryFlagSafely(analyticsData.topCountry),
    [analyticsData.topCountry],
  );

  return (
    <div className="max-w-[525px]">
      <FaviconLoader
        originalUrl={data.originalUrl}
        setFavicon={setLinkLogo}
        target={data.targetSite}
      />
      <div className="flex justify-between items-center mb-4">
        <div className="flex items-center gap-2">
          <img
            src={linkLogo}
            className="w-[33px] h-[33px]"
            alt="social Media Logo"
          />
          <label className="font-header text-[20px] font-medium">
            {getLinkLabel(data.targetSite)}
          </label>
        </div>
        <IconButton
          src="/assets/svgs/fi_cross.svg"
          onClick={handleDetailsModalClose}
          className="size-50"
        />
      </div>
      <p className="text-base break-all">
        {`${process.env.REACT_APP_WEBSITE_DOMAIN}/${data.shortUrl}`}
      </p>

      <div className="mb-4 mt-2 text-sm text-[#5890FF]">
        {data.tags?.map((tag, index) => (
          <span key={index} className="mr-2">
            {`#${tag}`}
          </span>
        ))}
      </div>

      <div className="flex gap-1 overflow-x-auto overflow-y-hidden">
        <div className="flex flex-col flex-grow border border-[#E5E9EB] p-[6px] lg:px-4 lg:py-2 rounded-xl bg-white">
          <span className="text-[10px] lg:text-xs font-content text-[#9B919D] whitespace-nowrap">
            {t('links.totalClicked')}
          </span>
          <div className="flex items-center gap-2">
            <span className="text-sm text-[#292828] lg:text-sm font-medium whitespace-nowrap">
              {analyticsData.totalClicked.count}
            </span>
          </div>
        </div>

        <div className="flex flex-col flex-grow border border-[#E5E9EB] p-[6px] lg:px-4 lg:py-2 rounded-xl bg-white">
          <span className="text-[10px] lg:text-xs font-content text-[#9B919D] whitespace-nowrap">
            {t('analytics.topCountry')}
          </span>
          <div className="flex items-center gap-1 lg:gap-2">
            <>
              <span className="text-[#292828] text-sm lg:text-sm font-medium whitespace-nowrap">
                {analyticsData.topCountry || t('common.noData')}
              </span>
              {analyticsData.topCountry && (
                <img
                  src={topCountryFlag}
                  alt="country flag"
                  className="h-4 w-4 lg:h-4 lg:w-4 rounded-full flex-shrink-0 max-w-fit"
                />
              )}
            </>
          </div>
        </div>

        <div className="flex flex-col flex-grow border border-[#E5E9EB] p-[6px] lg:px-4 lg:py-2 rounded-xl bg-white">
          <span className="text-[10px] lg:text-xs font-content text-[#9B919D] whitespace-nowrap">
            {t('analytics.topCity')}
          </span>
          <div className="flex items-center gap-1 lg:gap-2">
            <span className="text-[#292828] text-sm lg:text-sm font-medium whitespace-nowrap">
              {analyticsData.topCity || t('common.noData')}
            </span>
          </div>
        </div>

        <div className="flex flex-col flex-grow border border-[#E5E9EB] p-[6px] lg:px-4 lg:py-2 rounded-xl bg-white">
          <span className="text-[10px] lg:text-xs font-content text-[#9B919D] whitespace-nowrap">
            {!isMobile
              ? t('analytics.bestTimeToEngage')
              : t('analytics.highestTraffic')}
          </span>
          <div className="flex items-end gap-[2px] lg:gap-2">
            <>
              <span className="text-[#292828] text-sm lg:text-sm font-medium whitespace-nowrap">
                {analyticsData.bestTimeToEngage || t('common.noData')}
              </span>
              {analyticsData.bestTimeToEngage && (
                <span className="text-[10px] lg:text-xs font-content text-[#9B919D] whitespace-nowrap flex-shrink-0">
                  GMT
                </span>
              )}
            </>
          </div>
        </div>
      </div>

      <div className="w-full my-4">
        <LinkViewChart
          analyticsData={analyticsData}
          view={view}
          setView={setView}
        />
      </div>
      {isYoutubeLink && (
        <div>
          <YoutubeAnalytics
            interval={view}
            linkId={data._id}
            hasSubscription={hasSubscription}
            isConnectedWithYoutube={isConnectedWithYoutube}
          />
        </div>
      )}
    </div>
  );
};

export default LinkDetailsCard;
